import { useState, useEffect } from 'react';
import { makeRequest2 } from '../../../utils/makeRequest';
import { toastify } from '../../../utils/toast';
import { addSmsSettings, getSmsSettings } from '../../../utils/urls';
import { Button } from 'primereact/button';

const SMSTab = ({ facilityId }) => {
  const [smsSettings, setSmsSettings] = useState({
    user: '',
    senderId: '',
    apiKey: ''
  });
  const [smsList, setSmsList] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [formDisabled, setFormDisabled] = useState(false);

  useEffect(() => {
    if (facilityId) {
      console.log('Component mounted with facilityId:', facilityId);
      fetchSMSSettings();
    } else {
      console.log('No facilityId provided');
    }
  }, [facilityId]);

  const fetchSMSSettings = async () => {
    try {
      console.log('Fetching SMS settings from:', `${getSmsSettings}/${facilityId}`);
      const response = await makeRequest2(`${getSmsSettings}/${facilityId}`, 'GET');

      if (response && response.data && response.data.data) {
        console.log('SMS settings data:', response.data);
        const data = response.data.data ? [response.data.data] : [];
        console.log('Processed data for state:', data);
        setSmsList(data);

        if (data.length > 0) {
          setFormDisabled(true);
          console.log('Form disabled because settings exist');
        } else {
          setFormDisabled(false);
          console.log('Form enabled because no settings exist');
        }
      } else {
        console.log('No valid response data structure found:', response);
      }
    } catch (error) {
      console.error('Error fetching SMS settings:', error);
      toastify('Failed to fetch SMS settings', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = `${addSmsSettings}/${facilityId}${isEditing ? `/${smsSettings._id}` : ''}`;
      const method = isEditing ? 'PUT' : 'POST';
      const payload = { ...smsSettings };

      console.log('Submitting SMS settings to:', url, 'Method:', method);
      console.log('Payload:', payload);

      const response = await makeRequest2(url, method, payload);

      if (response.success || response.message) {
        toastify(`SMS settings ${isEditing ? 'updated' : 'added'} successfully`, 'success');
        fetchSMSSettings();
        resetForm();
      } else {
        toastify(response.message || 'Operation failed', 'error');
      }
    } catch (error) {
      console.error('Error saving SMS settings:', error);
      toastify('Error saving SMS settings', 'error');
    }
  };

  const handleEdit = (sms) => {
    setSmsSettings(sms);
    setIsEditing(true);
    setFormDisabled(false);
  };

  const resetForm = () => {
    setSmsSettings({
      user: '',
      senderId: '',
      apiKey: ''
    });
    setIsEditing(false);
    setFormDisabled(smsList.length > 0);
  };

  const truncateApiKey = (apiKey) => {
    if (!apiKey) return '';
    if (apiKey.length <= 8) return '********';
    return apiKey.substring(0, 4) + '********' + apiKey.substring(apiKey.length - 4);
  };

  return (
    <div className="row p-3">


      <div className="col-lg-6">
        <div className="card">
          <div className="card-body">
            <h5>{isEditing ? 'Update' : 'Add'} SMS Settings</h5>
            <form onSubmit={handleSubmit}>
              <div className="form-group mt-3">
                <label>User*</label>
                <input
                  type="text"
                  className="form-control"
                  value={smsSettings.user}
                  onChange={(e) => setSmsSettings({ ...smsSettings, user: e.target.value })}
                  required
                  disabled={formDisabled && !isEditing}
                />
              </div>

              <div className="form-group mt-3">
                <label>Sender ID*</label>
                <input
                  type="text"
                  className="form-control"
                  value={smsSettings.senderId}
                  onChange={(e) => setSmsSettings({ ...smsSettings, senderId: e.target.value })}
                  required
                  disabled={formDisabled && !isEditing}
                />
              </div>

              <div className="form-group mt-3">
                <label>API Key*</label>
                <input
                  type="password"
                  className="form-control"
                  value={smsSettings.apiKey}
                  onChange={(e) => setSmsSettings({ ...smsSettings, apiKey: e.target.value })}
                  required={!isEditing}
                  disabled={formDisabled && !isEditing}
                  placeholder={isEditing ? "Enter new API key or leave blank to keep current" : ""}
                />
              </div>

              <div className="mt-4 text-end">
                {isEditing && (
                  <button type="button" className="btn btn-secondary me-2" onClick={resetForm}>
                    Cancel
                  </button>
                )}
                {(!formDisabled || isEditing) && (
                  <button type="submit" className="btn btn-primary">
                    {isEditing ? 'Update' : 'Add'} SMS Settings
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="col-lg-6">
        <div className="card">
          <div className="card-body">
            <h5>Current SMS Settings</h5>
            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Sender ID</th>
                    <th>API Key</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {smsList.length > 0 ? (
                    smsList.map((sms, index) => (
                      <tr key={sms._id || index}>
                        <td>{sms.user || 'N/A'}</td>
                        <td>{sms.senderId || 'N/A'}</td>
                        <td>{truncateApiKey(sms.apiKey)}</td>
                        <td className="text-end">
                          <Button
                            icon="ti ti-pencil"
                            className="p-button-rounded p-button-warning me-2"
                            onClick={() => handleEdit(sms)}
                            tooltip="Edit"
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center py-4">
                        <div className="text-muted">
                          <i className="ti ti-inbox mb-2" style={{ fontSize: "24px" }}></i>
                          <p className="mb-0">No SMS settings available</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMSTab;
